import { TargetInfo, TargetLogicInfo, TmmEntry } from "@org-avp/avp-avengers-tmm-service-api";
import { useKeyFnState } from "@org-avp/avp-avengers-ui-shared-utils";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo } from "react";
import { tmmApi } from "../apis/tmm-service-api";
import { pollingInterval } from "../polling/pollingInterval";

interface TmmHelper {
  tmmEntries: TmmEntry[] | undefined;
  targetTmmEntries: TmmEntry[] | undefined;

  targetInfo: TargetInfo | undefined;
  targetLogic: TargetLogicInfo | undefined;
}

export const useTmmHelper = (tmmId: string, targetId?: string): TmmHelper => {
  const { data: getAllTmmEntriesResponse } = tmmApi.useGetAllTmmEntriesQuery(tmmId);
  const tmmEntries = useMemo(() => getAllTmmEntriesResponse?.tmmEntries, [getAllTmmEntriesResponse?.tmmEntries]);

  const tmmEntriesLastModifiedTimestamp = useMemo(
    () => getAllTmmEntriesResponse?.lastModified,
    [getAllTmmEntriesResponse?.lastModified],
  );

  // poll TmmUpdate: updates tmmEntries and lastModified of "getAllTmmEntries" query via API implementation
  tmmApi.useGetTmmUpdateQuery(tmmEntriesLastModifiedTimestamp ? [tmmId, tmmEntriesLastModifiedTimestamp] : skipToken, {
    pollingInterval: pollingInterval.tmm.getTmmUpdate,
  });

  // poll targetInfo: (lastModified timestamp of target + pinned/connected targets for current tmm)
  const { data: targetInfo } = tmmApi.useGetTargetInfoQuery(tmmId ? tmmId : skipToken, {
    pollingInterval: pollingInterval.tmm.getTargetInfo,
  });

  const [targetTmmEntries, setTargetTmmEntries] = useKeyFnState<TmmEntry[] | undefined>(undefined);
  useEffect(
    () => setTargetTmmEntries(targetId && tmmEntries ? tmmEntries.filter((entry) => entry.targetId === targetId) : undefined),
    [setTargetTmmEntries, targetId, tmmEntries],
  );

  const targetLogic = useMemo(
    () => targetId ? targetInfo?.targetLogic?.[targetId] : undefined,
    [targetId, targetInfo?.targetLogic],
  );

  return useMemo(
    () => ({ tmmEntries, targetTmmEntries, targetInfo, targetLogic }),
    [targetInfo, targetLogic, targetTmmEntries, tmmEntries],
  );
};
